import { Logo } from "./Logo";
import { LocationConfigurationContext } from "@/providers/LocationConfigurationProvider";
import { useContext } from "react";
import { cn } from "@/lib/utils";
import { ModeIndicator } from "./ModeIndicator";
import { getLabelForHours, OpeningHour } from "@/lib/labelForHours";
import { useLocationInfo } from "@/hooks/useLocationInfo";
import { locationRoute } from "@/App";

function Hero({
  showAddress = false,
  hours = [],
  showModeIndicator = true,
}: {
  showAddress?: boolean;
  hours?: OpeningHour[];
  showModeIndicator?: boolean;
}) {
  const { locationId } = locationRoute.useParams();
  const { data: locationInfo } = useLocationInfo(locationId);

  const name = locationInfo?.name || "";
  const { logo, contact } = useContext(LocationConfigurationContext);
  const hasLogo = !!logo;

  const locationOpeningHours = locationInfo?.openingHours || [];
  const openingHours =
    locationOpeningHours.length > 0
      ? (locationOpeningHours as OpeningHour[])
      : hours;
  const labelForHours = getLabelForHours(openingHours, new Date());

  return (
    <section className="bg-background max-md:min-h-[30vh] flex flex-col justify-center items-center md:items-start border-b">
      <div className="flex flex-col gap-y-2 gap-x-1 md:flex-row text-center text-foreground w-full">
        {hasLogo && <Logo />}

        <div className="flex flex-col gap-2 w-full justify-center text-sm items-center md:justify-start md:items-start md:border-l">
          <div className="flex-col gap-y-1 justify-center items-center md:justify-start md:items-start text-left flex md:pl-4 py-4">
            {
              <h1 className={cn("text-lg font-medium tracking-tighter")}>
                {name ?? ""}
              </h1>
            }

            <span className="text-[10px] text-muted-foreground tracking-tight text-center">
              Powered by{" "}
              <a
                href="https://table.taxi"
                target="_blank"
                className="underline"
              >
                Table Taxi
              </a>
            </span>
            {showModeIndicator && <ModeIndicator />}
          </div>

          <section className="flex flex-col gap-1 text-left items-start justify-center py-4 border-t w-full px-4 empty:border-none">
            {showAddress && contact?.address && (
              <div className="grid grid-cols-[14ch_auto] text-xs w-full gap-2 items-center">
                <span className="text-foreground/50">Address:</span>
                <address className="text-foreground/50 not-italic">
                  {contact.address}
                </address>
              </div>
            )}

            {showAddress && contact?.phone && (
              <div className="grid grid-cols-[14ch_auto] text-xs w-full gap-2 items-center">
                <span className="text-foreground/50">Phone:</span>
                <a href={`tel:${contact.phone}`} className="text-foreground/50">
                  {contact.phone}
                </a>
              </div>
            )}

            {openingHours.length > 0 && labelForHours.length > 0 && (
              <span className="text-xs w-full gap-2 items-center">
                <div className="flex gap-1 items-center">
                  <span className="text-primary">{labelForHours[0]}</span>
                  {labelForHours[1] && (
                    <>
                      <span className="block bg-primary size-1 rounded-full" />
                      <span className="text-foreground/50">
                        {labelForHours[1]}
                      </span>
                    </>
                  )}
                </div>
              </span>
            )}
          </section>
        </div>
      </div>
    </section>
  );
}

export { Hero };
